// Graphics quality levels. "Auto" picks a starting level from the hardware,
// then steps down if the phone can't hold the frame rate even at the lowest
// dynamic resolution (and back up once per session if it has headroom again).
// Ultra is for desktops with a real GPU driving a big screen: shadows, a
// post-process pipeline and reflections (see ultra.ts). Auto only starts there
// when the device is clearly such a desktop; phones never get it automatically.

export type QualityLevel = 'low' | 'medium' | 'high' | 'ultra';
export type QualityChoice = 'auto' | QualityLevel;

export interface QualityProfile {
  /** Cap on device pixel ratio. */
  maxDpr: number;
  /** Lowest dynamic resolution scale allowed. */
  minScale: number;
  /** Multiplier on tree density. */
  trees: number;
  /** Far clip distance, metres; the haze closes in to match. */
  viewDistance: number;
  /** Desktop effects (shadows, post-processing, reflections; see ultra.ts). */
  ultra?: boolean;
  /** Seconds dynamic resolution waits after start-up or a change (shader compiles stutter). */
  resolutionGrace?: number;
}

export const PROFILES: Record<QualityLevel, QualityProfile> = {
  // Full native resolution on a 4K/5K screen; dropping below ~70% looks worse
  // than stepping down to High, so Auto does that instead.
  ultra: { maxDpr: 3, minScale: 0.7, trees: 1, viewDistance: 3200, ultra: true, resolutionGrace: 5 },
  high: { maxDpr: 2, minScale: 0.55, trees: 1, viewDistance: 3200 },
  medium: { maxDpr: 1.5, minScale: 0.5, trees: 0.6, viewDistance: 2600 },
  low: { maxDpr: 1.2, minScale: 0.45, trees: 0.3, viewDistance: 1900 },
};

const LEVELS: QualityLevel[] = ['low', 'medium', 'high', 'ultra'];
const CHOICE_KEY = 'fone.quality';
const AUTO_KEY = 'fone.autoQuality';

export const QUALITY_LABELS: Record<QualityChoice, string> = { auto: 'Auto', ultra: 'Ultra', high: 'High', medium: 'Medium', low: 'Low' };

/** What the page can tell about the display and GPU (for the Ultra decision). */
export interface DesktopInfo {
  /** Mouse-like primary pointer and no touch screen as the primary input. */
  finePointer: boolean;
  /** Screen size in device pixels (CSS size × devicePixelRatio). */
  screenPixels: { width: number; height: number };
  /** Unmasked WebGL renderer string, e.g. "ANGLE (NVIDIA, NVIDIA GeForce RTX 3070 ...)". */
  gpu: string;
}

/** Discrete desktop GPUs (and Apple's larger chips) that handle Ultra at 4K. */
const STRONG_GPU = /GeForce (RTX|GTX (9[6-9]0|10[6-8]0|16[5-6]0))|NVIDIA RTX|Quadro RTX|Radeon (RX|Pro W)|Radeon VII|Arc A[57]|Apple M\d+ (Pro|Max|Ultra)/i;
/** Software rasterisers and integrated graphics: never Ultra automatically. */
const WEAK_GPU = /SwiftShader|llvmpipe|softpipe|Basic Render|Mali|Adreno|PowerVR|Intel/i;

/** True when the device is clearly a desktop with a decent GPU and a big screen. */
export function isCapableDesktop(info: DesktopInfo, nav: Partial<Navigator> = navigator): boolean {
  const ua = nav.userAgent ?? '';
  if (/Android|iPhone|iPad|iPod|Mobile/.test(ua)) return false;
  if (!info.finePointer || (nav.hardwareConcurrency ?? 0) < 6) return false;
  const { width, height } = info.screenPixels;
  if (Math.max(width, height) < 2560 || Math.min(width, height) < 1400) return false;
  return STRONG_GPU.test(info.gpu) && !WEAK_GPU.test(info.gpu);
}

/** Starting level for Auto from what the browser reveals about the device. */
export function guessLevel(nav: Partial<Navigator> & { deviceMemory?: number } = navigator, desktop?: DesktopInfo): QualityLevel {
  const ua = nav.userAgent ?? '';
  const memory = nav.deviceMemory;
  const cores = nav.hardwareConcurrency ?? 8;
  if (desktop && isCapableDesktop(desktop, nav)) return 'ultra';
  // iPhones from the last several years all handle High; older ones step down at runtime.
  if (/iPhone|iPad|iPod|Macintosh/.test(ua)) return 'high';
  if (/Android/.test(ua)) {
    if ((memory !== undefined && memory <= 3) || cores <= 4) return 'low';
    if (memory !== undefined && memory >= 8) return 'high';
    return 'medium';
  }
  return 'high';
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not persisted; fine.
  }
}

function isLevel(v: string | null): v is QualityLevel {
  return v === 'low' || v === 'medium' || v === 'high' || v === 'ultra';
}

export class Quality {
  choice: QualityChoice;
  /** Level Auto has settled on for this device. */
  private autoLevel: QualityLevel;
  private readonly guess: QualityLevel;
  /** Ultra is offered in the cycle (desktops); phones keep the old Auto/High/Medium/Low. */
  private readonly allowUltra: boolean;
  onChange: ((level: QualityLevel) => void) | undefined;

  // Auto adjustment state.
  private windowTime = 0;
  private windowFrames = 0;
  private slowWindows = 0;
  private fastWindows = 0;
  private sinceChange = 0;
  private upgraded = false;
  private downgraded = false;

  constructor(guess = guessLevel(), allowUltra = guess === 'ultra') {
    const c = read(CHOICE_KEY);
    this.choice = c === 'auto' || isLevel(c) ? c : 'auto';
    const a = read(AUTO_KEY);
    this.guess = guess;
    this.allowUltra = allowUltra;
    // Auto only stays on Ultra while the device still qualifies for it.
    this.autoLevel = isLevel(a) && (a !== 'ultra' || guess === 'ultra') ? a : guess;
  }

  get level(): QualityLevel {
    return this.choice === 'auto' ? this.autoLevel : this.choice;
  }

  get profile(): QualityProfile {
    return PROFILES[this.level];
  }

  /** Cycle Auto → Ultra → High → Medium → Low. */
  cycle(): void {
    const order: QualityChoice[] = this.allowUltra ? ['auto', 'ultra', 'high', 'medium', 'low'] : ['auto', 'high', 'medium', 'low'];
    this.set(order[(order.indexOf(this.choice) + 1) % order.length]!);
  }

  set(choice: QualityChoice): void {
    const before = this.level;
    this.choice = choice;
    write(CHOICE_KEY, choice);
    this.resetWindows();
    if (this.level !== before) this.onChange?.(this.level);
  }

  /**
   * Call every frame while the car is being driven. `atMinScale` is true when
   * dynamic resolution has nothing left to give.
   */
  observe(frameDt: number, resolutionScale: number, atMinScale: boolean): void {
    if (this.choice !== 'auto') return;
    this.sinceChange += frameDt;
    this.windowTime += frameDt;
    this.windowFrames++;
    if (this.windowTime < 1) return;
    const fps = this.windowFrames / this.windowTime;
    this.windowTime = 0;
    this.windowFrames = 0;
    // Give the dynamic resolution time to settle after start-up or a change.
    if (this.sinceChange < 6) return;
    this.slowWindows = fps < 48 && atMinScale ? this.slowWindows + 1 : 0;
    this.fastWindows = fps > 58 && resolutionScale >= 1 ? this.fastWindows + 1 : 0;
    const i = LEVELS.indexOf(this.autoLevel);
    if (this.slowWindows >= 4 && i > 0) {
      this.downgraded = true;
      this.shift(LEVELS[i - 1]!);
    } else if (this.fastWindows >= 60 && !this.upgraded && !this.downgraded && i < LEVELS.indexOf(this.guess)) {
      // A minute of full-resolution 60 fps: try one level up (once per session).
      this.upgraded = true;
      this.shift(LEVELS[i + 1]!);
    }
  }

  private shift(level: QualityLevel): void {
    this.autoLevel = level;
    write(AUTO_KEY, level);
    this.resetWindows();
    this.onChange?.(level);
  }

  private resetWindows(): void {
    this.windowTime = 0;
    this.windowFrames = 0;
    this.slowWindows = 0;
    this.fastWindows = 0;
    this.sinceChange = 0;
  }
}
